import React, { useState, useEffect, useCallback } from 'react';
import {
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { ActionButton } from '../../components/Button';
import UserTableFilter from './components/UserTableFilter';
import UserCard from './components/UserCard';
import UserTable from './components/UserTable';
import UserPagination from './components/UserPagination';
import UserCardSkeleton from './components/UserCardSkeleton';
import UserTableSkeleton from './components/UserTableSkeleton';
import { userService, User as ApiUser } from '../../services/userService';
import { User } from './types/UserTypes';

const UserManagement = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // State management
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [showBulkActions, setShowBulkActions] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [loading, setLoading] = useState(true);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [resultsPerPage] = useState(10);

    // Fetch users from API
    const fetchUsers = useCallback(async (page: number = 1) => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await userService.getUsers({
                page,
                limit: resultsPerPage,
            });

            // Transform API data to match UI interface
            const transformedUsers: User[] = response.data.map((apiUser: ApiUser) => ({
                id: parseInt(apiUser.id.replace(/-/g, '').substring(0, 8), 16), // Convert UUID to number
                apiId: apiUser.id, // Keep original API ID for backend operations
                status: apiUser.is_active ? 'Active' : 'InActive',
                username: apiUser.username,
                roles: userService.mapRolesToDisplay(apiUser.roles),
                changed: userService.formatTimeDifference(apiUser.last_updated),
                access: userService.formatDate(apiUser.last_access_at),
            }));

            setUsers(transformedUsers);
            setTotalResults(response.total);
            setTotalPages(Math.ceil(response.total / resultsPerPage));
            setCurrentPage(response.page);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch users');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    }, [resultsPerPage]);

    // Load users on component mount
    useEffect(() => {
        fetchUsers(1);
    }, [fetchUsers]);

    // Handlers
    const handleSelectUser = (userId: number, selected: boolean) => {
        if (selected) {
            setSelectedUsers(prev => [...prev, userId]);
        } else {
            setSelectedUsers(prev => prev.filter(id => id !== userId));
        }
    };

    const handleSelectAll = (selected: boolean) => {
        if (selected) {
            setSelectedUsers(users.map(user => user.id));
        } else {
            setSelectedUsers([]);
        }
    };

    const handleBulkAction = async (action: string) => {
        try {
            setBulkActionLoading(true);
            setError(null);

            // Get the API user IDs for the selected users
            const selectedApiUsers = users.filter(user => selectedUsers.includes(user.id));
            const userIds = selectedApiUsers.map(user => user.apiId);

            await userService.bulkAction({
                user_ids: userIds,
                action: action as 'block' | 'unblock' | 'assign_role' | 'remove_role',
            });

            // Refresh the user list to show updated status
            await fetchUsers(currentPage);
            
            // Clear selection
            setSelectedUsers([]);
            setShowBulkActions(false);
            
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to perform bulk action');
            console.error('Error performing bulk action:', err);
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleCloseBulkActions = () => {
        setIsAnimating(true);
        setTimeout(() => {
            setSelectedUsers([]);
            setShowBulkActions(false);
            setIsAnimating(false);
        }, 300); // Match animation duration
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchUsers(page);
    };

    // Show bulk actions when users are selected
    useEffect(() => {
        if (selectedUsers.length > 0 && !showBulkActions) {
            setShowBulkActions(true);
        } else if (selectedUsers.length === 0 && showBulkActions && !isAnimating) {
            setIsAnimating(true);
            setTimeout(() => {
                setShowBulkActions(false);
                setIsAnimating(false);
            }, 300); // Match animation duration
        }
    }, [selectedUsers, showBulkActions, isAnimating]);

    // Show error message if there's an error
    if (error) {
        return (
            <div style={{
                backgroundColor: '#FEFBFF',
                minHeight: '100vh',
                position: window.innerWidth < 600 ? 'absolute' : 'relative',
                top: window.innerWidth < 600 ? '96px' : 'auto',
                left: window.innerWidth < 600 ? '0' : 'auto',
                right: window.innerWidth < 600 ? '0' : 'auto',
                bottom: window.innerWidth < 600 ? '0' : 'auto',
                padding: '16px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: window.innerWidth < 600 ? '100vw' : '100%',
                boxSizing: 'border-box'
            }}>
                <Typography color="error" variant="h6">
                    {error}
                </Typography>
            </div>
        );
    }

    return (
        <div style={{
            backgroundColor: '#FEFBFF',
            minHeight: window.innerWidth < 600 ? 'calc(100vh - 96px)' : '100vh',
            position: window.innerWidth < 600 ? 'absolute' : 'relative',
            top: window.innerWidth < 600 ? '86px' : 'auto',
            left: window.innerWidth < 600 ? '0' : 'auto',
            right: window.innerWidth < 600 ? '0' : 'auto',
            bottom: window.innerWidth < 600 ? '0' : 'auto',
            padding: '16px',
            paddingBottom: window.innerWidth < 600 ? '32px' : '16px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: window.innerWidth < 600 ? '100vw' : '100%',
            boxSizing: 'border-box',
            overflowY: 'auto',
            overflowX: 'hidden'
        }}>
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: isMobile?'16px':'32px',
                paddingBottom: window.innerWidth < 600 ? '24px' : '16px',
                width: '100%',
                maxWidth: '100%',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: window.innerWidth < 600 ? '16px' : '0'
            }}>
                {/* Header */}
                <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center' 
                    flexWrap="nowrap"
                    mb={2}
                    spacing={2}
                    sx={{
                        width: '100%',
                        overflow: 'hidden',
                    }}
                >
                    <Typography
                        fontSize={{ xs: 20, sm: 22, md: 24 }}
                        fontWeight={600}
                        fontFamily='Poppins, sans-serif'
                        color='#132F5F'
                        textAlign='left'
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        Users
                    </Typography>

                    <ActionButton
                        onClick={() => navigate('/add-user')}
                        startIcon={<AddIcon />}
                        sx={{
                            backgroundColor: '#132F5F',
                            fontSize: { xs: '12px', sm: '14px' },
                            px: 2,
                            py: 1,
                            mt: 0,
                            height: "56px",
                            minWidth: '120px', 
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            '&:hover': {
                                backgroundColor: '#132F5F',
                            },
                        }}
                    >
                        Add New User
                    </ActionButton>
                </Stack>

                {/* Bulk Actions */}
                {showBulkActions && (
                    <UserTableFilter
                        selectedCount={selectedUsers.length}
                        onClose={handleCloseBulkActions}
                        onApply={handleBulkAction}
                        isAnimating={isAnimating}
                        loading={bulkActionLoading}
                    />
                )}

                {/* User List */}
                {loading ? (
                    // Show skeleton loaders while loading
                    isMobile ? (
                        <Stack spacing={2}>
                            {[1, 2, 3, 4].map((index) => (
                                <UserCardSkeleton key={index} />
                            ))}
                        </Stack>
                    ) : (
                        <UserTableSkeleton />
                    )
                ) : (
                    // Show actual content when loaded
                    isMobile ? (
                        <Stack spacing={2}>
                            {users.map((user) => (
                                <UserCard
                                    key={user.id}
                                    user={user}
                                    isSelected={selectedUsers.includes(user.id)}
                                    onSelect={handleSelectUser}
                                />
                            ))}
                        </Stack>
                    ) : (
                        <UserTable
                            users={users}
                            selectedUsers={selectedUsers}
                            onSelectUser={handleSelectUser}
                            onSelectAll={handleSelectAll}
                        />
                    )
                )}

                {/* Pagination */}
                {!loading && (
                    <UserPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalResults={totalResults}
                        resultsPerPage={resultsPerPage}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </div>
    );
};

export default UserManagement;
